import getFieldValue from '../elements/getFieldValue';
import match from '../conditions/match';

let i18nCache;

/**
 * Given a form and the name of a phrase, queries the phrases object
 * and all its conditional sections to find the appropriate value.
 *
 * @param {Element} form - The main form containing the fields we can query
 * @param {string} dottedKey - The dotted i18n key we want to query
 * @returns {string|undefined} The string phrase found
 */
export function t(form, dottedKey) {
  // navigate into i18n object tree using dotted key
  const { context, key } = getI18nContext(dottedKey, getI18n(form));
  if(!key || !context) { return; }

  // merge any conditional sections
  const mergedContext = getMergedContext(form, context);
  const value = mergedContext[key.toLowerCase().replace(/-\s/g, '_').replace(/'/g, '')];

  if(!value) { return; }

  // if key is capitalized, then capitalize output
  if(key.match(/^[A-Z]/)) {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  return value;
}

/**
 * Digs into the phrases object and merges in all conditional sections that
 * are matched by field values on the current form.
 *
 * @param {Element} form - The main form containing the fields we can query
 * @param {Object} context - The current object of i18n key-values
 * @returns {Object} - A key-value list of phrases and values with conditions merged
 */
function getMergedContext(form, context) {
  const output = Object.assign({}, context);

  Object
    .entries(context)
    .filter(entry => (
      // get conditional sections of the phrases object
      entry[0].includes('=') && typeof(entry[1]) === 'object'
    ))
    .filter(entry => {
      /**
       * CONDITIONAL PHRASES
       *
       * This works very similarly to the conditional YAML that Atlas uses for routes etc.
       * For each locale, we have a list of default phrases, then a serious of conditional
       * objects which are used to override the defaults with new values if those conditions
       * are met.
       *
       * The conditions are queried by looking for one or more form fields and seeing if
       * they return one of the given comma-separated values. If so, the condition is matched
       * and its values are returned.
       */
      const regexMatch = entry[0].match(/^(.+)=(.+)$/);
      const fieldNames = regexMatch[1].split(/,\s*/);
      const testValue = regexMatch[2];

      // any of the fields (on the left) have a value that matches the conditions (on the right)
      return fieldNames.some(fieldName => (
        match(getFieldValue(form, fieldName), testValue)
      ));
    })
    .forEach(entry => {
      // merge in all the satisfied conditional sections into the main object
      Object.assign(output, entry[1]);
    });

  return output;
}

/**
 *
 * @param {string} dottedKey - The i18n key in dotted notation including
 * @returns {{ context, key }} - An object containing the new i18n context and the key to query
 */
function getI18nContext(dottedKey, context) {
  const keys = String(dottedKey).split('.');
  keys.slice(0, -1).forEach(key => { context = context && context[key]; });
  return { context, key: keys[keys.length - 1] };
}

/**
 * Retrieves the i18n object which was saved from the server in form_wizard.mustache
 * and transforms it into something we can parse here. In the future we may wish
 * to extend it to more than 'phrases', but for now this works.
 * @returns {object}
 */
function getI18n() {
  if(i18nCache) return i18nCache;

  const leadFormI18n = (window.components || {}).leadFormI18n || {};
  const phrases = leadFormI18n.phrases || {};
  const conditions = leadFormI18n.conditional_phrases || [];
  i18nCache = {
    phrases: {
      ...phrases,
      ...conditions.reduce((obj, item) => ({ ...obj, [item.condition]: item.phrases }), {})
    }
  };

  return i18nCache;
}
